import React, { useState, useEffect, useMemo, useRef, useCallback, Suspense } from 'react';
import './MarketHeatmap.css';
import { layoutTwoLevel } from '../utils/treemap';

// Same reason as GradeCard: this renders on the landing page.
const MarketMovingAverages = React.lazy(() => import('./MarketMovingAverages'));

// Major stocks per sector with approximate market cap weights
const HEATMAP_SYMBOLS = {
  Technology: ['AAPL', 'MSFT', 'NVDA', 'AVGO', 'ORCL', 'CRM', 'AMD', 'ADBE', 'CSCO', 'INTC'],
  Healthcare: ['LLY', 'UNH', 'JNJ', 'ABBV', 'MRK', 'TMO', 'ABT', 'PFE', 'AMGN', 'ISRG'],
  Financials: ['JPM', 'V', 'MA', 'BAC', 'GS', 'MS', 'BLK', 'SCHW', 'C', 'WFC'],
  'Consumer Disc.': ['AMZN', 'TSLA', 'HD', 'NKE', 'SBUX', 'TGT', 'LOW', 'BKNG'],
  Communication: ['META', 'GOOGL', 'NFLX', 'DIS', 'CMCSA', 'T', 'VZ', 'TMUS'],
  Energy: ['XOM', 'CVX', 'COP', 'SLB', 'EOG', 'MPC', 'OXY'],
  Industrials: ['CAT', 'HON', 'UNP', 'BA', 'RTX', 'DE', 'GE', 'LMT'],
  'Consumer Staples': ['PG', 'KO', 'PEP', 'WMT', 'COST', 'PM', 'CL'],
};

const SECTOR_NAMES = Object.keys(HEATMAP_SYMBOLS);

const SECTOR_COLORS = {
  Technology: '#6495ED',
  Healthcare: '#10B981',
  Financials: '#7B68EE',
  'Consumer Disc.': '#F59E0B',
  Communication: '#EC4899',
  Energy: '#F97316',
  Industrials: '#8B5CF6',
  'Consumer Staples': '#06B6D4',
};

function getChangeColor(change) {
  if (change >= 3) return '#10B981';
  if (change >= 1) return '#34D399';
  if (change >= 0) return '#6EE7B7';
  if (change >= -1) return '#FCA5A5';
  if (change >= -3) return '#EF4444';
  return '#DC2626';
}

// Tap a heatmap tile → this modal pops the ticker's moving-average chart
// (price + SMA10/EMA21/SMA50/SMA200), reusing the same component the
// Correlations page uses. Robinhood-style "see something move, click to chart it".
const HEATMAP_CHART_WINDOWS = [30, 90, 180, 365];

function HeatmapChartModal({ ticker, onClose }) {
  // Default to a tighter 90-day view; the MA component slices client-side so
  // switching windows is instant (no refetch).
  const [windowDays, setWindowDays] = useState(90);
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card, #1A1F2E)',
          border: '1px solid var(--border-medium, #334155)',
          borderRadius: 16,
          maxWidth: 820,
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}
      >
        {/* Pinned action bar — day-range toggle (left) + close (right). Stays
            put while the chart body scrolls, so neither control is ever out of
            reach. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '0.75rem 1rem',
            borderBottom: '1px solid var(--border-subtle, #2A3548)',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
            {HEATMAP_CHART_WINDOWS.map((w) => (
              <button
                key={w}
                onClick={() => setWindowDays(w)}
                style={{
                  padding: '4px 12px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  border: '1px solid var(--border-medium, #334155)',
                  background: windowDays === w ? 'var(--accent-primary, #6495ED)' : 'transparent',
                  color: windowDays === w ? '#0B0F19' : 'var(--text-secondary, #94A3B8)',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                }}
              >
                {w}D
              </button>
            ))}
          </div>
          <button
            onClick={onClose}
            aria-label="Close chart"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted, #64748B)',
              fontSize: 26,
              lineHeight: 1,
              cursor: 'pointer',
              padding: '0 4px',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>
        {/* Scrollable chart body */}
        <div style={{ overflowY: 'auto', padding: '0.85rem 1.1rem 1.1rem', flex: 1 }}>
          <Suspense fallback={<div aria-busy="true" />}>
          <MarketMovingAverages
            tickers={[ticker]}
            title={`${ticker} Moving Averages`}
            windowDays={windowDays}
            chartHeight={300}
          />
          </Suspense>
        </div>
      </div>
    </div>
  );
}

export default function MarketHeatmap() {
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showHeatmapInfo, setShowHeatmapInfo] = useState(false);
  const [selectedSectors, setSelectedSectors] = useState(new Set(SECTOR_NAMES));
  const [tooltip, setTooltip] = useState(null);
  const [chartTicker, setChartTicker] = useState(null);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  const roRef = useRef(null);
  const chartNodeRef = useRef(null);

  // Callback ref — fires when the chart div enters/leaves the DOM
  const chartRef = useCallback(node => {
    chartNodeRef.current = node;
    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }
    if (node) {
      const ro = new ResizeObserver(entries => {
        const { width, height } = entries[0].contentRect;
        setContainerSize({ w: width, h: height });
      });
      ro.observe(node);
      roRef.current = ro;
    }
  }, []);

  const toggleSector = (sector) => {
    setSelectedSectors(prev => {
      const allSelected = prev.size === SECTOR_NAMES.length;
      let next;
      if (allSelected) {
        next = new Set([sector]);
      } else if (prev.size === 1 && prev.has(sector)) {
        next = new Set(SECTOR_NAMES);
      } else {
        next = new Set(prev);
        if (next.has(sector)) {
          if (next.size > 1) next.delete(sector);
        } else {
          next.add(sector);
        }
      }
      console.log('[Heatmap] toggleSector:', sector, '→ selected:', [...next]);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedSectors(new Set(SECTOR_NAMES));
  };

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const allSymbols = Object.values(HEATMAP_SYMBOLS).flat();
        const batchSize = 20;
        const allQuotes = {};

        for (let i = 0; i < allSymbols.length; i += batchSize) {
          const batch = allSymbols.slice(i, i + batchSize).join(',');
          const resp = await fetch(`/api/market/quotes?symbols=${batch}`);
          if (resp.ok) {
            const data = await resp.json();
            (data.quotes || data || []).forEach(q => {
              if (q.symbol) allQuotes[q.symbol] = q;
            });
          }
        }

        if (cancelled) return;

        const treeData = Object.entries(HEATMAP_SYMBOLS).map(([sector, symbols]) => ({
          name: sector,
          children: symbols.map(sym => {
            const q = allQuotes[sym] || {};
            const change = q.changePercentage ?? q.changesPercentage ?? q.change_percent ?? q.todaysChangePerc ?? 0;
            const cap = q.marketCap || q.market_cap || 1000000000;
            return {
              name: sym,
              size: Math.max(cap / 1e9, 1),
              change: typeof change === 'number' ? change : 0,
            };
          }),
        }));

        setHeatmapData(treeData);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  const filteredData = useMemo(() => {
    if (!heatmapData) return null;
    const filtered = heatmapData.filter(sector => selectedSectors.has(sector.name));
    console.log('[Heatmap] filteredData:', filtered.map(s => s.name), 'cells:', filtered.reduce((n, s) => n + s.children.length, 0));
    return filtered;
  }, [heatmapData, selectedSectors]);

  const cells = useMemo(() => {
    if (!filteredData || containerSize.w === 0) return [];
    return layoutTwoLevel(filteredData, containerSize.w, containerSize.h);
  }, [filteredData, containerSize]);

  const handleMouseEnter = useCallback((e, cell) => {
    const rect = chartNodeRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltip({
      name: cell.name,
      change: cell.change,
      sector: cell.sector,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, []);

  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  // Esc closes the chart modal.
  useEffect(() => {
    if (!chartTicker) return;
    const onKey = (e) => { if (e.key === 'Escape') setChartTicker(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chartTicker]);

  const allSelected = selectedSectors.size === SECTOR_NAMES.length;

  return (
    <div className="heatmap-section">
      {chartTicker && (
        <HeatmapChartModal ticker={chartTicker} onClose={() => setChartTicker(null)} />
      )}
      <div className="heatmap-header">
        <h2 className="heatmap-title">Market Heatmap</h2>
        <button
          className="info-btn"
          onClick={() => setShowHeatmapInfo(prev => !prev)}
          title="Why this matters"
          style={{ marginLeft: '0.5rem', marginRight: 'auto' }}
        >
          &#9432;
        </button>
        <button className="heatmap-toggle" onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? 'Show' : 'Hide'}
        </button>
      </div>

      {showHeatmapInfo && (
        <div className="heatmap-info-panel">
          <div className="heatmap-info-header">
            <span className="heatmap-info-title">Why the Heatmap Matters</span>
            <button className="heatmap-info-close" onClick={() => setShowHeatmapInfo(false)}>&times;</button>
          </div>
          <p>The heatmap shows today's performance of the largest stocks organized by sector. The size of each tile reflects the company's market capitalization, and the color intensity shows the magnitude of price change.</p>
          <p><strong>What to look for:</strong></p>
          <ul>
            <li><strong>Broad green or broad red</strong> — tells you whether the overall market is risk-on or risk-off today. When most tiles are deep red, the Turbulence Index is likely elevated.</li>
            <li><strong>Sector rotation</strong> — when one sector is green while others are red, money is rotating. This can signal shifts in investor preference (e.g., from growth to defensives).</li>
            <li><strong>Concentrated vs. distributed gains</strong> — if only a few large tiles are green while most are flat or red, market breadth is narrow. Narrow breadth is a warning sign that the rally may not be sustainable.</li>
          </ul>
        </div>
      )}

      {!collapsed && (
        <>
          <div className="heatmap-sector-chips">
            <button
              className={`sector-chip chip-all ${allSelected ? 'active' : ''}`}
              onClick={selectAll}
            >
              All
            </button>
            {SECTOR_NAMES.map(sector => (
              <button
                key={sector}
                className={`sector-chip ${selectedSectors.has(sector) ? 'active' : ''}`}
                style={{ '--chip-color': SECTOR_COLORS[sector] }}
                onClick={() => toggleSector(sector)}
              >
                {sector}
              </button>
            ))}
          </div>

          {loading && <div className="heatmap-loading">Loading market heatmap...</div>}
          {error && <div className="heatmap-error">Failed to load heatmap: {error}</div>}

          {filteredData && (
            <>
              <div className="heatmap-legend">
                <span className="heatmap-legend-item" style={{ color: '#DC2626' }}>-3%+</span>
                <span className="heatmap-legend-item" style={{ color: '#EF4444' }}>-1 to -3%</span>
                <span className="heatmap-legend-item" style={{ color: '#FCA5A5' }}>0 to -1%</span>
                <span className="heatmap-legend-item" style={{ color: '#6EE7B7' }}>0 to +1%</span>
                <span className="heatmap-legend-item" style={{ color: '#34D399' }}>+1 to +3%</span>
                <span className="heatmap-legend-item" style={{ color: '#10B981' }}>+3%+</span>
              </div>

              <p className="heatmap-disclaimer">Based on most recent market close. Data may be delayed.</p>

              <div className="heatmap-chart" ref={chartRef}>
                {cells.map((cell, i) => {
                  const bg = getChangeColor(cell.change || 0);
                  const showLabel = cell.w > 38 && cell.h > 22;
                  const showChange = cell.w > 50 && cell.h > 36;
                  // Mid-size tiles (e.g. CSCO 66x34) had room for one line but
                  // showed only the ticker; put the % beside it there.
                  const inlineChange = !showChange && showLabel && cell.w > 56;
                  const pct = `${cell.change >= 0 ? '+' : ''}${cell.change.toFixed(1)}%`;
                  return (
                    <div
                      key={`${cell.name}-${cell.sector}`}
                      className="heatmap-cell"
                      style={{
                        left: cell.x,
                        top: cell.y,
                        width: cell.w - 2,
                        height: cell.h - 2,
                        background: bg,
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => handleMouseEnter(e, cell)}
                      onMouseMove={(e) => handleMouseEnter(e, cell)}
                      onMouseLeave={handleMouseLeave}
                      onClick={() => setChartTicker(cell.name)}
                      title={`${cell.name} ${pct} · click to chart`}
                    >
                      {showLabel && (
                        <span className="heatmap-cell-label">
                          {cell.name}
                          {inlineChange && <span className="heatmap-cell-change-inline"> {pct}</span>}
                        </span>
                      )}
                      {showChange && (
                        <span className="heatmap-cell-change">{pct}</span>
                      )}
                    </div>
                  );
                })}

                {tooltip && (
                  <div
                    className="heatmap-tooltip"
                    style={{
                      left: tooltip.x + 12,
                      top: tooltip.y - 40,
                    }}
                  >
                    <strong>{tooltip.name}</strong>
                    <span className={tooltip.change >= 0 ? 'ht-positive' : 'ht-negative'}>
                      {tooltip.change >= 0 ? '+' : ''}{tooltip.change.toFixed(2)}%
                    </span>
                    <span className="ht-sector">{tooltip.sector}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
